import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { TrendingUp } from "lucide-react";

const usd0 = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

function fmtDay(iso) {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-charcoal text-bone px-3 py-2 rounded shadow-lg text-xs">
      <p className="font-mono text-[10px] uppercase tracking-widest text-bone/85">{fmtDay(p.date)}</p>
      <p className="font-mono text-sm mt-0.5">{usd0(p.revenue)}</p>
      <p className="text-[10px] text-bone/85">{p.orders} order{p.orders === 1 ? "" : "s"}</p>
    </div>
  );
}

export default function SalesChart() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/sales-chart?days=30");
        setData(data);
      } catch { /* ignore — chart is optional */ }
    })();
  }, []);

  if (!data) {
    return (
      <div className="mb-6 border border-borderSubtle bg-white h-40 animate-pulse" data-testid="sales-chart-loading"/>
    );
  }

  return (
    <div className="mb-6 border border-borderSubtle bg-white" data-testid="sales-chart">
      <div className="flex items-center justify-between px-6 pt-5">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-full bg-bone flex items-center justify-center text-charcoal">
            <TrendingUp size={14} strokeWidth={2}/>
          </span>
          <div>
            <p className="overline">30-day revenue</p>
            <p className="font-mono text-2xl text-oxblood mt-1 leading-none">
              {usd0(data.total_revenue)}
            </p>
          </div>
        </div>
        <p className="text-xs text-stone font-mono">
          {data.total_orders} order{data.total_orders === 1 ? "" : "s"}
        </p>
      </div>
      <div className="h-32 mt-2" data-testid="sales-chart-svg">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data.series} margin={{ top: 0, right: 12, left: 12, bottom: 8 }}>
            <defs>
              <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4A0E17" stopOpacity={0.35}/>
                <stop offset="95%" stopColor="#4A0E17" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: "#78716C", fontFamily: "monospace" }}
              tickFormatter={fmtDay}
              interval="preserveStartEnd"
              minTickGap={40}
              tickLine={false}
              axisLine={false}
            />
            <YAxis hide domain={[0, "dataMax"]}/>
            <Tooltip content={<ChartTooltip/>} cursor={{ stroke: "#4A0E17", strokeOpacity: 0.2 }}/>
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#4A0E17"
              strokeWidth={2}
              fill="url(#revGradient)"
              dot={false}
              activeDot={{ r: 4, fill: "#4A0E17", stroke: "#F9F8F6", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
